import { Collapse, Typography, Card, Space, Button, Empty, Descriptions, Divider } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { GRAPH_IPFS_BASEURL, GRAPH_V2_IPFS_BASEURL } from "../../config/StaticConfig";

const { Panel } = Collapse;
const { Link, Paragraph, Text } = Typography;

interface Props {
  subgraph: {
    createdAt: number;
    id: string;
    identifier: string;
    cluster: string;
    name: string;
    projectId: string;
    deployUrl: string;
  };
  project: {
    id: string;
    name: string;
    secret: string;
  };
}

const labelColor = {
  color: "grey",
};

export function SubgraphDeployment({ project, subgraph }: Props) {
  const cluster = subgraph.cluster;

  const ipfsBaseUrl = cluster === 'v1' ? GRAPH_IPFS_BASEURL : GRAPH_V2_IPFS_BASEURL;

  return project && subgraph ? (
    <Card
      title="Deployment"
      className={"card-body"}
      style={{ flex: 1 }}
      extra={
        <Button onClick={() => window.open("https://github.com/graphprotocol/graph-cli")}>
          View Documentation <RightOutlined />
        </Button>
      }
    >
      {project ? (
        <Descriptions layout="vertical" colon={false} column={1}>
          <Descriptions.Item label="Access token" labelStyle={labelColor}>
            <Typography.Text copyable>{project.secret}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Deploy URL" labelStyle={labelColor}>
            <Typography.Text copyable>{subgraph.deployUrl}</Typography.Text>
          </Descriptions.Item>
          <Divider />
        </Descriptions>
      ) : null}

      <Collapse bordered={true} defaultActiveKey={["1"]} style={{ borderRadius: 10 }}>
        {/* Prerequisite */}
        <Panel header="Install Graph CLI" key="1">
          <Space direction="vertical">
            Note: CLI version needs to match with the subgraph apiVersion
            <Text>Using NPM</Text>
            <Paragraph>
              <Text code copyable>
                npm install -g @graphprotocol/graph-cli@latest
              </Text>
            </Paragraph>
            <Text>Using Yarn</Text>
            <Paragraph>
              <Text code copyable>
                yarn global add @graphprotocol/graph-cli@latest
              </Text>
            </Paragraph>
          </Space>
        </Panel>
        {/* Clone subgraph example */}
        <Panel header="Clone the subgraph example (optional)" key="2">
          <Space direction="vertical" style={{ padding: 10 }}>
            <Text>Clone subgraph example</Text>
            <Paragraph>
              <Text code copyable>
                git clone https://github.com/evelynCRO/cronos-subgraph-example.git
              </Text>
            </Paragraph>
            <Text>Install dependencies</Text>
            <Paragraph>
              <Text code copyable>
                yarn install
              </Text>
            </Paragraph>
            <Text>Generate the files for subgraph deployment</Text>
            <Paragraph>
              <Text code copyable>
                yarn codegen
              </Text>
            </Paragraph>
            <Text>Build subgraph project</Text>
            <Paragraph>
              <Text code copyable>
                yarn build
              </Text>
            </Paragraph>
          </Space>
        </Panel>
        {/* Config */}
        <Panel header="Config subgraph.yml and package.json" key="3">
          <Space direction="vertical" style={{ padding: 10 }}>
            subgraph.yml:
            <Paragraph>
              <Text code>apiVersion</Text>: 0.0.7
            </Paragraph>
            <Paragraph>
              <Text code>network</Text>: Needs to be either
              <Text code>cronos</Text>
              or
              <Text code>cronos-testnet</Text>.
            </Paragraph>
            <Paragraph>
              <Text code>startBlock</Text>: Please specify it to the block in which the contract was
              created or "1" for the genesis block.{" "}
              <Link
                href="https://thegraph.com/docs/en/developer/create-subgraph-hosted/#the-subgraph-manifest"
                target="_blank"
              >
                Learn more
              </Link>
            </Paragraph>
            package.json library version:
            <Paragraph>
              <Text code copyable>
                "@graphprotocol/graph-cli": "latest"
              </Text>
            </Paragraph>
            <Paragraph>
              <Text code copyable>
                "@graphprotocol/graph-ts": "latest"
              </Text>
            </Paragraph>
          </Space>
        </Panel>
        <Panel header="Deploy subgraph" key="4">
          <Space direction="vertical" style={{ padding: 10 }}>
            Command arguments:
            <Paragraph>
              <Text code copyable>
                {
                  "graph deploy {subgraph_id} -—access-token={project_secret} --node {graph_node_url} --ipfs {ipfs_api_url}"
                }
              </Text>
            </Paragraph>
            Deploy subgraph command:
            <Text code copyable>
              {`graph deploy ${subgraph.id} --access-token=${project.secret} --node ${subgraph.deployUrl} --ipfs ${ipfsBaseUrl}`}
            </Text>
          </Space>
        </Panel>
      </Collapse>
    </Card>
  ) : (
    <Empty />
  );
}
